// src/components/Clinicmanagement/Clinicmanagement.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/axios.secure';
import Menu from '../Menu/Menu';
import './css/clinicmanagement.css';

// ───────────────── 허용 문자 정의 (앱과 동일 규칙) ─────────────────
const MAX_ID_LEN = 28;
const MAX_PW_LEN = 32;

// ID: 영문/숫자 + . - _
const ID_CHAR_REGEX = /^[A-Za-z0-9._-]+$/;   // 전체 문자열 검사용
const ID_CHAR_ONE   = /^[A-Za-z0-9._-]$/;    // 한 글자 검사용

// 비밀번호: 영문/숫자 + !@#$%^*()_+-=
const PW_CHAR_ONE     = /^[A-Za-z0-9!@#$%^*()_+\-=]$/;   // 한 글자 검사용
const PW_ALLOWED_ALL  = /^[A-Za-z0-9!@#$%^*()_+\-=]+$/;  // 전체 문자열 검사용

// ───────────────── 값 필터링 (붙여넣기/드래그입력 대비) ─────────────────
const filterIdValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => ID_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_ID_LEN);

const filterPwValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => PW_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_PW_LEN);

function StatusBadge({ status }) {
  const s = String(status || '').toUpperCase();
  const cls = s === 'ACTIVE' ? 'active' : 'locked';
  return <span className={`status-pill ${cls}`}>{s || '-'}</span>;
}

// 프런트 1차 비번검증 (서버와 동일 규칙)
function strongPwOk(pw, id) {
  if (!pw || typeof pw !== 'string') return false;
  if (pw.length < 10 || pw.length > 32) return false; // 프론트에서 10~32자로 제한
  if (/\s/.test(pw)) return false;

  // 허용되지 않은 문자 포함 여부 (앱 규칙과 동일)
  if (!PW_ALLOWED_ALL.test(pw)) return false;

  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSpec  = /[^A-Za-z0-9]/.test(pw);
  if ([hasLower, hasUpper, hasDigit, hasSpec].filter(Boolean).length < 3) return false;
  if (id && pw.toLowerCase().includes(String(id).toLowerCase())) return false;
  return true;
}

function Clinicmanagement() {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2200); };

  // 리스트 상태
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (size || 10))),
    [total, size]
  );

  // 행별 비밀번호 입력 상태
  const [pwMap, setPwMap] = useState({}); // { [admin_id]: 'newPw' }

  // 생성 상태
  const [createForm, setCreateForm] = useState({
    admin_id: '',
    admin_password: '',
    status: 'ACTIVE',
  });

  // ───────────────── 입력 차단용 핸들러들 ─────────────────
  const handleBeforeInputAdminId = (e) => {
    if (e.isComposing) return; // 한글 조합 중이면 통과

    const text = e.data;
    if (!text) return; // Backspace / Delete 등

    // 여러 글자가 한 번에 들어오는 경우(붙여넣기 등)도 각 글자 검사
    for (const ch of text) {
      if (!ID_CHAR_ONE.test(ch)) {
        e.preventDefault();
        return;
      }
    }
    if (e.target.value.length >= MAX_ID_LEN) {
      e.preventDefault(); // 길이 초과 방지
    }
  };

  // 검색창도 admin_id 기준 → ID 규칙 재사용
  const handleBeforeInputSearch = (e) => {
    if (e.isComposing) return;

    const text = e.data;
    if (!text) return;

    for (const ch of text) {
      if (!ID_CHAR_ONE.test(ch)) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleBeforeInputPassword = (e) => {
    if (e.isComposing) return;

    const text = e.data;
    if (!text) return;
    for (const ch of text) {
      if (!PW_CHAR_ONE.test(ch)) {
        e.preventDefault();
        return;
      }
    }
    if (e.target.value.length >= MAX_PW_LEN) {
      e.preventDefault();
    }
  };

  // 응답 파서
  const parsePayload = (data) => {
    if (!data) return {};
    const payload =
      Array.isArray(data?.data) ? data :
      (data?.data && typeof data.data === 'object') ? data.data : data;
    const rows =
      Array.isArray(payload?.data) ? payload.data :
      Array.isArray(payload?.list) ? payload.list : null;
    const pageV  = Number(payload?.page ?? data?.page ?? 1);
    const sizeV  = Number(payload?.size ?? data?.size ?? 10);
    const totalV = Number(payload?.total ?? payload?.count ?? data?.total ?? 0);
    return { rows, pageV, sizeV, totalV };
  };

  // 목록
  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, size };
      const trimmed = (q || '').trim();

      if (trimmed.length >= 2) {
        params.q = trimmed;  // 2자 이상일 때만 서버 검색 파라미터 설정
      }

      const { data } = await api.get('/manager/user/clinic', { params });
      const { rows, pageV, sizeV, totalV } = parsePayload(data);

      let nextRows = Array.isArray(rows) ? rows : [];

      // 🔍 서버가 q를 무시하는 경우를 대비해서, 프론트에서도 한 번 더 필터
      if (trimmed.length >= 2) {
        const tLower = trimmed.toLowerCase();
        nextRows = nextRows.filter((r) =>
          String(r.admin_id || '').toLowerCase().includes(tLower)
        );
      }

      setList(nextRows);
      setPage(pageV || 1);
      setSize(sizeV || 10);
      setTotal(totalV || 0);

      if (trimmed && trimmed.length < 2) {
        toast('검색은 2자 이상 입력 시 적용됩니다.');
      }
    } catch (e) {
      setList([]);
      setTotal(0);
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line
  }, []);

  // 상태 변경
  const updateStatus = async (adminId, nextStatus) => {
    setLoading(true);
    try {
      await api.patch(`/manager/user/clinic/${encodeURIComponent(adminId)}/status`, {
        status: nextStatus,
      });
      toast('상태 변경 완료');
      setList((prev) =>
        prev.map((r) =>
          r.admin_id === adminId ? { ...r, status: nextStatus } : r
        )
      );
    } catch (e) {
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('상태 변경 실패');
    } finally {
      setLoading(false);
    }
  };

  // 비번 초기화 (행 단위)
  const resetPasswordFor = async (adminId) => {
    const newPw = pwMap[adminId] || '';
    if (!strongPwOk(newPw, adminId)) {
      return toast(
        '비밀번호는 10~32자, 공백금지, (대/소/숫/특) 3종류 이상, ID 포함 금지, 허용 특수문자만 사용 가능'
      );
    }
    setLoading(true);
    try {
      await api.post(
        `/manager/user/clinic/${encodeURIComponent(adminId)}/password`,
        { new_password: newPw }
      );
      toast('비밀번호 초기화 완료');
      setPwMap((m) => ({ ...m, [adminId]: '' }));
    } catch (e) {
      if (e?.response?.status === 406 || e?.response?.status === 409) {
        toast('서버에서 비밀번호 정책에 맞지 않는 것으로 판단했습니다.');
      } else if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('초기화 실패');
    } finally {
      setLoading(false);
    }
  };

  // 생성
  const createClinic = async () => {
    const { admin_id, admin_password, status } = createForm;
    if (!admin_id || !admin_password) return toast('ID/비밀번호를 입력하세요');

    // ID 형식 체크 (앱과 동일)
    if (!ID_CHAR_REGEX.test(admin_id)) {
      return toast('ID는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }

    if (!strongPwOk(admin_password, admin_id)) {
      return toast(
        '비밀번호는 10~32자, 공백금지, (대/소/숫/특) 3종류 이상, ID 포함 금지, 허용 특수문자만 사용 가능'
      );
    }

    setLoading(true);
    try {
      await api.post('/manager/user/create/clinic', {
        admin_id,
        admin_password,
        status: status || 'ACTIVE',
      });
      toast('생성 완료');
      setCreateForm({ admin_id: '', admin_password: '', status: 'ACTIVE' });
      fetchList(1);
    } catch (e) {
      if (e?.response?.status === 409) toast('중복된 계정입니다.');
      else if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else if (e?.response?.status === 406) toast('비밀번호 정책 위반');
      else toast('생성 실패');
    } finally {
      setLoading(false);
    }
  };

  const confirmAnd = async (msg, fn) => {
    if (window.confirm(msg)) await fn();
  };

  return (
    <Menu>
      <div className="account-page cm">
        <h1 className="text-2xl font-bold">클리닉 계정 관리</h1>

        {/* 리스트 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">계정 목록</h2>

          {/* 검색/컨트롤 */}
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex gap-2 flex-1">
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="admin_id 검색 (2자 이상)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fetchList(1);
                }}
                onBeforeInput={handleBeforeInputSearch}
              />
              <select
                className="border rounded px-3 py-2"
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(1);
                }}
                aria-label="페이지당 개수"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}개
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => fetchList(1)}
                disabled={loading}
                className="px-4 py-2 rounded border border-slate-400 text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors"
              >
                검색
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border rounded mt-2">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">admin_id</th>
                  <th className="text-left p-2">authority</th>
                  <th className="text-left p-2">status</th>
                  <th className="text-left p-2">created_at</th>
                  <th className="text-left p-2">pw_changed_at</th>
                  <th className="text-left p-2">actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.admin_id} className="border-t">
                    <td className="p-2 font-medium">{row.admin_id}</td>
                    <td className="p-2">{row.authority}</td>
                    <td className="p-2">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="p-2">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="p-2">
                      {row.pw_changed_at
                        ? new Date(row.pw_changed_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="p-2">
                      {/* 상태 토글 */}
                      {row.status === 'ACTIVE' ? (
                        <button
                          className="px-2 py-1 rounded border text-black mr-2"
                          onClick={() =>
                            confirmAnd(
                              '해당 계정을 잠금(LOCK) 처리할까요?',
                              () => updateStatus(row.admin_id, 'LOCKED')
                            )
                          }
                        >
                          잠금
                        </button>
                      ) : (
                        <button
                          className="px-2 py-1 rounded bg-green-600 text-black mr-2"
                          onClick={() =>
                            confirmAnd(
                              '해당 계정을 활성화(ACTIVE)로 변경할까요?',
                              () => updateStatus(row.admin_id, 'ACTIVE')
                            )
                          }
                        >
                          해제
                        </button>
                      )}

                      {/* 비번 초기화 */}
                      <input
                        type="password"
                        className="border rounded px-2 py-1 mr-2"
                        placeholder="새 비밀번호"
                        value={pwMap[row.admin_id] || ''}
                        onChange={(e) =>
                          setPwMap((m) => ({
                            ...m,
                            [row.admin_id]: filterPwValue(e.target.value),
                          }))
                        }
                        onBeforeInput={handleBeforeInputPassword}
                        style={{ width: 180 }}
                      />
                      <button
                        className="px-2 py-1 rounded border"
                        onClick={() =>
                          confirmAnd(
                            '비밀번호를 변경하시겠습니까?',
                            () => resetPasswordFor(row.admin_id)
                          )
                        }
                      >
                        변경
                      </button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td
                      className="p-4 text-center text-gray-500"
                      colSpan={6}
                    >
                      데이터가 없습니다
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {total}건 / {page} / {totalPages} 페이지
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page <= 1 || loading}
                onClick={() => fetchList(page - 1)}
              >
                이전
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page >= totalPages || loading}
                onClick={() => fetchList(page + 1)}
              >
                다음
              </button>
            </div>
          </div>
        </section>

        {/* 생성 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">클리닉 계정 생성</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="border rounded px-3 py-2"
              placeholder="admin_id"
              value={createForm.admin_id}
              onChange={(e) =>
                setCreateForm((f) => ({
                  ...f,
                  admin_id: filterIdValue(e.target.value),
                }))
              }
              onBeforeInput={handleBeforeInputAdminId}
            />
            <input
              type="password"
              className="border rounded px-3 py-2"
              placeholder="password (10~32자, 공백X)"
              value={createForm.admin_password}
              onChange={(e) =>
                setCreateForm((f) => ({
                  ...f,
                  admin_password: filterPwValue(e.target.value),
                }))
              }
              onBeforeInput={handleBeforeInputPassword}
            />
            <select
              className="border rounded px-3 py-2"
              value={createForm.status}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="LOCKED">LOCKED</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={createClinic}
              disabled={loading}
              className="px-4 py-2 rounded border text-black disabled:opacity-50"
            >
              생성
            </button>
            <button
              onClick={() =>
                setCreateForm({
                  admin_id: '',
                  admin_password: '',
                  status: 'ACTIVE',
                })
              }
              className="px-4 py-2 rounded border"
            >
              초기화
            </button>
          </div>
          <p className="text-xs text-gray-500">
            * 비밀번호는 10~32자, 공백 금지, 대/소문자/숫자/허용 특수문자 중 3종류 이상 포함, ID 포함 금지
          </p>
        </section>

        {msg && <div className="cm-toast">{msg}</div>}
      </div>
    </Menu>
  );
}

export default Clinicmanagement;
